import { supabase } from "./supabase.js";
import {
  vendorRollup, budgetPosture, highRisks, isOpenRisk, isOverdue,
  invoiceSummary, teamStats, forecastAtCompletion, programPosture, burnVsScope, grossMargin,
} from "./calc.js";

/* The portfolio, loaded for leadership.
 *
 * The single-contract screens pull one register at a time for one contract.
 * The portfolio wants seven registers across every contract at once, and the
 * naive way, a loop of contract by register, is dozens of round trips. So it
 * is seven queries total: each register table once, for the whole set of
 * contract ids, and the rows are bucketed by contract here. The database
 * already refuses an admin nothing and a member everything outside their
 * assignments, so what comes back is exactly what the caller may see.
 *
 * The heavy lifting is the fetch; everything after it is the same pure
 * arithmetic the rest of the app uses, so a program is scored on the portfolio
 * exactly as it is scored on its own Overview.
 */

const REGISTERS = ["deliverables", "risks", "invoices", "expenses", "resources", "vendors",
                   "decisions", "compliance_items"];

export async function loadPortfolioRows(contractIds) {
  if (!contractIds?.length) return {};
  const out = Object.fromEntries(contractIds.map((id) => [id, emptyBuckets()]));

  const results = await Promise.all(REGISTERS.map((table) =>
    supabase.from(table).select("*").in("contract_id", contractIds).is("deleted_at", null)));

  REGISTERS.forEach((table, i) => {
    const { data, error } = results[i];
    if (error) return;               // a missing register is not a reason to fail the page
    for (const row of data ?? []) {
      const bucket = out[row.contract_id];
      if (bucket) bucket[table].push(row);
    }
  });
  return out;
}

const emptyBuckets = () => ({
  deliverables: [], risks: [], invoices: [], expenses: [], resources: [], vendors: [],
  decisions: [], compliance_items: [],
});

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

/* On federal recovery work the documentation is the deliverable: if the file
   is not clean the cost gets disallowed later, after it is spent. So the
   compliance register becomes a live completeness score, not a yes or no. A
   requirement marked Compliant counts as done; At risk and Non-compliant are
   the problems a leader wants pulled out. */
function complianceScore(items = []) {
  const total = items.length;
  if (!total) return { total: 0, complete: 0, pct: null, problems: 0, level: "none" };
  const complete = items.filter((i) => /compliant/i.test(i.status || "") && !/non/i.test(i.status || "")).length;
  const problems = items.filter((i) => /non-?compliant|at risk/i.test(i.status || "")).length;
  const pct = Math.round((complete / total) * 100);
  return {
    total, complete, pct, problems,
    level: problems > 0 || pct < 70 ? "red" : pct < 90 ? "amber" : "ok",
  };
}

/* One contract's worth of headline numbers, from its rows. Everything a card
   on the portfolio shows, and the inputs the posture is scored from. */
export function programSummary(contract, rows = emptyBuckets()) {
  /* Merge over the defaults so a caller that passes only some registers, or a
     register the database has not created yet, cannot crash the page. */
  rows = { ...emptyBuckets(), ...rows };
  const rollup = vendorRollup(rows.deliverables);
  const budget = budgetPosture(contract, rows.expenses, rows.resources);
  const high = highRisks(rows.risks, contract);
  const openRisks = rows.risks.filter(isOpenRisk);
  const overdueCDs = rows.deliverables.filter(
    (d) => isOverdue(d) && d.is_contract_deliverable).length;
  const invoices = invoiceSummary(rows.invoices, {
    vendors: rows.vendors, contractTerms: contract.payment_terms_days,
  });
  const team = teamStats(rows.resources);
  const pct = rollup.total.percentComplete;
  const forecast = forecastAtCompletion({
    ceiling: budget.ceiling, spent: budget.spent, percentComplete: pct,
  });
  const pace = burnVsScope({
    ceiling: budget.ceiling, spent: budget.spent,
    startDate: contract.start_date, endDate: contract.end_date, percentComplete: pct,
  });
  const compliance = complianceScore(rows.compliance_items);
  const openDecisions = rows.decisions.filter((d) => (d.status || "Open") !== "Closed");
  /* The money that matters: funded now versus the ceiling, and whether a mod
     is in flight to raise it. "Close to the ceiling with nothing in flight" is
     the silent killer. */
  const funded = num(contract.funded_value);
  const headroom = budget.ceiling ? budget.ceiling - budget.spent : null;
  const posture = programPosture({
    budgetLevel: budget.level, forecastLevel: forecast.level,
    overdueCDs, highRisks: high.length, paceLevel: pace.level,
    compliance: compliance.level, modExposed: funded > 0 && budget.spent > funded && !contract.mod_in_flight,
  });
  return {
    contract,
    ceiling: budget.ceiling,
    funded: funded || null,
    fundingSource: contract.funding_source?.trim() || null,
    modInFlight: !!contract.mod_in_flight,
    sensitivity: contract.public_sensitivity || null,
    spent: budget.spent,
    spentSource: budget.spentSource,
    financialsAsOf: budget.asOf,
    margin: grossMargin(contract),
    pm: contract.pm_name?.trim() || null,
    headroom,
    percentComplete: pct,
    budget, forecast, pace, posture,
    compliance,
    openDecisions: openDecisions.length,
    decisions: openDecisions,
    highRisks: high.length,
    openRisks: openRisks.length,
    overdueCDs,
    pastTermsCount: invoices.pastTermsCount,
    pastTermsDollars: invoices.pastTermsDollars,
    worstInvoices: invoices.worst,
    pastInvoices: invoices.past,
    fte: team.fte,
    headcount: team.headcount,
  };
}

/* Group the summaries region first, then client within region, the way the
   portfolio is read. Unlabelled programs fall into honest buckets rather than
   vanishing: a contract with no territory set is still money and risk on the
   books and must not drop off a leadership page. */
export function groupPortfolio(summaries) {
  const byRegion = new Map();
  for (const s of summaries) {
    const region = s.contract.territory?.trim() || "Location not set";
    const client = s.contract.client?.trim() || "Client not set";
    if (!byRegion.has(region)) byRegion.set(region, new Map());
    const clients = byRegion.get(region);
    if (!clients.has(client)) clients.set(client, []);
    clients.get(client).push(s);
  }

  const regions = [...byRegion.entries()].map(([region, clients]) => {
    const programs = [...clients.values()].flat();
    return {
      region,
      clients: [...clients.entries()]
        .map(([client, items]) => ({ client, programs: sortWorstFirst(items) }))
        .sort((a, b) => worstRank(b.programs) - worstRank(a.programs)
          || a.client.localeCompare(b.client)),
      totals: rollupTotals(programs),
      rank: worstRank(programs),
    };
  });

  /* A region with something red sorts above a calm one, and "Location not set"
     is pushed to the bottom whatever it holds, because it is a data gap to fix
     rather than a place to lead with. */
  regions.sort((a, b) => {
    const aUnset = a.region === "Location not set";
    const bUnset = b.region === "Location not set";
    if (aUnset !== bUnset) return aUnset ? 1 : -1;
    return b.rank - a.rank || a.region.localeCompare(b.region);
  });

  return { regions, totals: rollupTotals(summaries) };
}

const sortWorstFirst = (items) =>
  [...items].sort((a, b) => b.posture.rank - a.posture.rank
    || (b.overdueCDs - a.overdueCDs)
    || a.contract.name.localeCompare(b.contract.name));

const worstRank = (programs) => programs.reduce((m, p) => Math.max(m, p.posture.rank), 0);

export function rollupTotals(summaries) {
  const t = {
    programs: summaries.length,
    red: 0, amber: 0, green: 0,
    value: 0, spent: 0, forecast: 0, forecastKnown: 0, ceilingForKnown: 0,
    overdueCDs: 0, highRisks: 0, pastTermsDollars: 0,
    fte: 0, fteKnown: 0, headcount: 0,
  };
  for (const s of summaries) {
    t[s.posture.key] += 1;
    t.value += s.ceiling || 0;
    t.spent += s.spent || 0;
    t.overdueCDs += s.overdueCDs;
    t.highRisks += s.highRisks;
    t.pastTermsDollars += s.pastTermsDollars || 0;
    t.headcount += s.headcount;
    if (s.forecast.eac !== null) {
      t.forecast += s.forecast.eac;
      t.forecastKnown += 1;
      /* The ceiling of the same programs, so variance compares like with like
         rather than all ceilings against some forecasts. */
      t.ceilingForKnown += s.ceiling || 0;
    }
    if (s.fte !== null) { t.fte += s.fte; t.fteKnown += 1; }
  }
  /* Portfolio variance only means something where a ceiling and a forecast
     both exist. Summing forecasts that include "too early" would understate
     the exposure and read as precision that is not there. */
  t.variance = t.forecastKnown ? t.ceilingForKnown - t.forecast : null;
  t.fte = t.fteKnown ? Math.round(t.fte * 10) / 10 : null;
  return t;
}
